import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import { FormService } from '../../../api/form.service';
import { TranslateService } from '../../../api/translate.service';
import uuid from '../../../utils/uuid';
import { DataObject } from '../../../interfaces/data-objects';

@Component({
  selector: 'iv-aml-checklist-question',
  templateUrl: './aml-checklist-question.component.html',
  styleUrls: ['./aml-checklist-question.component.scss']
})
export class AmlChecklistQuestionComponent implements OnInit {
  @Input() amlQuestion: DataObject;
  @Input() canEditAml = true;
  @Input() disabled = false;
  @Input() showValidation = false;
  @Input() userCountry: string;

  @Output() saveData: EventEmitter<any>;
  
  // INFO: Used on the template and resetNoteField method.
  amlQuestionObj: DataObject; 
  amlChecklistForm: UntypedFormGroup;
  uuid = uuid();

  constructor(
    private fb: UntypedFormBuilder,
    private formService: FormService,
    private translateService: TranslateService,
  ) {
    this.saveData = new EventEmitter();
  }

  ngOnInit(): void {
    this.setupForm();
  }

  setupForm() {
    if (this.amlQuestion) {
      const amlChecklistObject = {};
      this.amlQuestionObj = this.amlQuestion?.aml_question || {};

      // INFO: Will create a custom form field based on the question key.
      if (this.amlQuestionObj.options && this.amlQuestionObj.options.length > 0) {
        amlChecklistObject[this.amlQuestionObj.question] = [{
          value: this.amlQuestion.aml_answer,
          disabled: this.isDisabled()
        },
        Validators.required];          
      }

      // INFO: Will create a custom form field based on the display_note_field key.
      if (this.amlQuestionObj.display_note_field) {
        amlChecklistObject[this.amlQuestionObj.question + '_note'] = [{
          value: this.amlQuestion.aml_notes,
          disabled: this.isDisabled()
        },
        Validators.required];          
      }
      
      this.amlChecklistForm = this.fb.group(amlChecklistObject);
      this.formService.addLoadedForm(this.amlChecklistForm, this.uuid);

      if (this.showValidation === true && this.amlChecklistForm) {
        this.formService.showFormValidations(this.amlChecklistForm);
      }

      this.setupFormSubscription();
    }
  }

  isDisabled(): boolean {
    return this.disabled || !this.canEditAmlFlag;
  }

  set canEditAmlFlag(value: boolean) {
    this.canEditAml = value;
  }

  get canEditAmlFlag() {
    return this.canEditAml;
  }

  setupFormSubscription() {
    this.amlChecklistForm.valueChanges
      .pipe(
        untilDestroyed(this),
        debounceTime(500),
        distinctUntilChanged((originalObj, newObj) => { 
          return JSON.stringify(originalObj) === JSON.stringify(newObj)
        }),
        map((changes: any) => this.resetNoteField(changes)),
      )
      .subscribe((changes) => {
        const amlAnswerToSave = this.getAmlAnswerToSave(changes);
        this.saveData.emit({ 'action': 'saveAmlChecklist', 'data': amlAnswerToSave });
      });
  }

  getAmlAnswerToSave(changes: DataObject): DataObject {
    return {
      id: this.amlQuestion.id,
      tempcustomer: this.amlQuestion.tempcustomer,
      description: this.amlQuestionObj.description,
      question: this.amlQuestionObj.question,
      aml_answer: changes[this.amlQuestionObj.question],
      aml_notes: changes[this.amlQuestionObj.question + '_note'],
    };
  }

  getQuestionValue(questionKey: string): any {
    if (this.amlChecklistForm && this.amlChecklistForm.get(questionKey)) {
      return this.amlChecklistForm.get(questionKey).value;
    }
    return null;
  }

  /**
   * INFO: Toggles the Note field based on the condition and the radio button value.
   * Conditions: always | yes | no
  */
  toggleNoteField(condition: string, questionKey: string): boolean {
    const questionValue = this.getQuestionValue(questionKey);
    if (questionValue === null) return false;
    switch (condition) {
      case 'always':
        return true;
      case 'yes':
        return questionValue;
      case 'no':
        return !questionValue;
    }
  }

  /**
   * INFO: If a question is found, it means has options like (yes, no, n/a) to whether or not display a note.
   * The note field needs to be wiped out once is toggled.
   * */
  resetNoteField(changes: any): any {
    const changesKeys = Object.keys(changes);
    if (changesKeys.includes(this.amlQuestionObj.question)) {
      if (this.amlQuestionObj.display_note_field && !this.toggleNoteField(this.amlQuestionObj.display_note_field_when, this.amlQuestionObj.question)) {
        changes[this.amlQuestionObj.question + '_note'] = null;
        this.amlChecklistForm.patchValue({ [this.amlQuestionObj.question + '_note']: null }, { emitEvent: false });
      }
    }
    return changes;
  }

  getTranslation(key: string): string {
    return this.translateService.getTranslation(key.toUpperCase());
  }

}
